import classNames from 'classnames'
import React, { useContext } from 'react'
import { useLocation } from 'react-router-dom'

import { AuthSessionContext } from '@context/AuthContext'

import { isSignUpPath } from '@utils/path'

import { MenuSection } from './MenuSection'
import { useActionSections } from './menuSections/useActionSections'
import {
  MenuSection as MenuSectionType,
  useAnonymousMenuSections,
  useMenuSections,
} from './menuSections/useMenuSections'

import styles from './Menu.module.scss'

interface MenuContentProps {
  sections: MenuSectionType[]
  onScroll?: React.UIEventHandler<HTMLUListElement>
  showShadow?: boolean
}

const MenuContent: React.FC<MenuContentProps> = ({ sections, onScroll, showShadow }) => (
  <div className={classNames(styles.menuContent, { [styles.scrolled]: showShadow })}>
    <nav className={styles.menuNavigation}>
      <ul className={styles.menuList} onScroll={onScroll}>
        {sections.map((section) => (
          <MenuSection key={section.id} section={section} isTopLevel />
        ))}
      </ul>
    </nav>
  </div>
)

const MenuAuthed: React.FC<{ onScroll?: React.UIEventHandler<HTMLUListElement> }> = ({
  onScroll,
}) => {
  const { menuSections } = useMenuSections()
  return <MenuContent sections={menuSections} onScroll={onScroll} />
}

const MenuAnon: React.FC<{ onScroll?: React.UIEventHandler<HTMLUListElement> }> = ({
  onScroll,
}) => {
  const { menuSections } = useAnonymousMenuSections()
  return <MenuContent sections={menuSections} onScroll={onScroll} />
}

export const Menu: React.FC<{ onScroll?: React.UIEventHandler<HTMLUListElement> }> = ({
  onScroll,
}) => {
  const [auth] = useContext(AuthSessionContext)
  const location = useLocation()

  const isSignUp = isSignUpPath(location.pathname)
  const isTwoFactorConfirmPage = location.pathname.includes('/2fa/confirm')
  const isAuthed = !!auth && !(isSignUp || isTwoFactorConfirmPage)

  return isAuthed ? <MenuAuthed onScroll={onScroll} /> : <MenuAnon onScroll={onScroll} />
}

const ActionsMenuAuthed: React.FC<{ showShadow?: boolean }> = ({ showShadow }) => {
  const { actionSections } = useActionSections()
  return <MenuContent sections={actionSections} showShadow={showShadow} />
}

export const ActionsMenu: React.FC<{ showShadow?: boolean }> = ({ showShadow }) => {
  const [auth] = useContext(AuthSessionContext)
  const location = useLocation()

  const isSignUp = isSignUpPath(location.pathname)
  const isTwoFactorConfirmPage = location.pathname.includes('/2fa/confirm')
  const isAuthed = !!auth && !(isSignUp || isTwoFactorConfirmPage)

  if (!isAuthed) {
    return null
  }
  return <ActionsMenuAuthed showShadow={showShadow} />
}
